/**
 * ClubSetupWizardPage — 6-step guided onboarding for clubs.
 *
 * Steps:
 *  1. Dados Institucionais (legal name, type, sport, location)
 *  2. Contatos-Chave (key people with roles)
 *  3. Estrutura Esportiva (sports, categories, teams)
 *  4. Módulos & Serviços (select platform features)
 *  5. Convidar Equipe (send invitations with roles)
 *  6. Revisão & Ativar (summary → activate)
 *
 * Note: Official data linking (Vínculo de Resultados) was removed from the
 * wizard and moved to a separate Claim flow (/manager/claim) to reduce
 * onboarding friction. See SPEC_OFFICIAL_DATA_CLAIM.md.
 *
 * Route: /manager/setup
 * Guard: GESTOR role
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { VUKZ, VUKZ_GRADIENT } from '../../theme/vukzTheme';
import {
  Building2, Users, Dumbbell, Package, UserPlus, CheckCircle2,
  ChevronRight, ChevronLeft, Loader2, Search, X, Plus, Minus, Trash2,
  MapPin, Globe, Instagram, Phone, Mail, AlertCircle,
  Sparkles, ArrowRight, Check, Info, Shield, Lock, Database,
  UserCog, Link2
} from 'lucide-react';
import {
  startOnboarding, getOnboardingState, saveStep,
  saveContacts,
  saveSportsStructure, saveInvites,
  completeOnboarding, discardOnboarding,
  ORG_TYPES, SPORT_OPTIONS, CATEGORY_OPTIONS, CONTACT_ROLES,
  INVITE_ROLES,
} from '../../services/onboardingService';
import { getPublicPlans, formatBRL } from '../../services/publicPlansService';
import OnboardingAssistant from '../../components/onboarding/OnboardingAssistant';
import { ActionType } from '../../components/onboarding/agentActions';
import { useAssistFormBridge } from '../../contexts/AssistFormBridge';
import {
  buildClubSetupFormContext, applyClubSetupPatch,
} from '../../components/assist/clubSetupFormContext';
import GatedAction from '../../components/ui/GatedAction';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';
import { useRBAC } from '../../contexts/RBACContext';

/* ═══════════════════════════════════════════════════════════════════════════
   WIZARD i18n
   ═══════════════════════════════════════════════════════════════════════════ */
const WizardLangCtx = React.createContext('pt');
const useWizardT = () => {
  const lang = React.useContext(WizardLangCtx);
  return getWizardT(lang);
};
const getWizardT = (lang) => lang === 'en' ? {
  step1Label:'Institution', step1Desc:'Club data',
  step2Label:'Contacts', step2Desc:'Key people',
  step3Label:'Structure', step3Desc:'Sports & categories',
  step4Label:'Tech Staff', step4Desc:'Coaches & staff',
  step5Label:'Roster', step5Desc:'Add athletes',
  step6Label:'Plan', step6Desc:'Plan & limits',
  step7Label:'Invites', step7Desc:'Invite people',
  step8Label:'Activate', step8Desc:'Review & activate',
  s1Title:'Club Data', s1Sub:'Basic information about your organization',
  s1Info:"Fill in your club's data to create your presence on the platform. Fields marked with * are required.",
  legalName:'Official Name (Legal Entity)', tradeName:'Trade Name',
  orgType:'Organization Type', country:'Country', state:'State', city:'City',
  primarySport:'Primary Sport', primarySportSub:"What is the club's primary sport?",
  s2Title:'Key Contacts', s2Sub:'Add the people responsible for managing the club',
  s2Info:'The VUKZ Master Admin will be the first system administrator. Add at least 1 contact to proceed.',
  roleLabel:'Role', fullName:'Full Name', jobTitle:'Job Title',
  emailLabel:'Email', phoneLabel:'Phone', primaryContact:'Primary contact',
  removeContact:'Remove contact', addContact:'Add Contact',
  s3Title:'Sports Structure', s3Sub:'Select the sports and categories your club offers',
  s3Info:'For each sport, select all offered age categories. You can add more than one sport for multi-sport clubs.',
  offeredCats:'Offered categories', selectAll:'select all',
  athleteCount:'# Athletes', coachCount:'# Coaches', supportStaff:'Support Staff',
  trainingVenue:'Training Venue', ownFacility:'Own Facility', usesVideo:'Uses Video',
  usesSensors:'Uses Sensors', usesSpreadsheets:'Uses Spreadsheets', currentSoftware:'Current Software',
  addAnotherSport:'Add Another Sport',
  linkOfficialData:'Link to Official Data',
  linkOfficialDataDesc:"To access athletes and results from your club's database, you can request the official link after completing registration.",
  requestLink:'Request Link',
  s4Title:'Technical Staff', s4Sub:'Add coaches, S&C coaches and other professionals.',
  addProfessional:'Add Professional', noProfessionals:'No professionals added yet.',
  clickAddProfessional:"Click 'Add Professional' to get started.",
  editProfessional:'Edit Professional', newProfessional:'New Professional',
  fullNameLabel:'Full name', roleStarLabel:'Role *',
  assignedCategories:'Assigned categories',
  cancel:'Cancel', save:'Save', add:'Add', edit:'Edit',
  s5Title:'Roster', s5Sub:'Add athletes and assign to categories and technical staff.',
  addAthlete:'Add Athlete', allCategories:'All categories',
  noAthletes:'No athletes added yet.', athleteMinInfo:'Minimum: name, date of birth, gender, category.',
  editAthlete:'Edit Athlete', newAthlete:'New Athlete',
  dob:'Date of birth', genderLabel:'Gender *', male:'Male', female:'Female',
  categoryLabel:'Category *', mainCoach:'Main Coach', scCoach:'S&C Coach', none:'None',
  s6Title:'Plan & Features', s6Sub:'Features and limits derive from the plan you choose',
  s6Info:'Pick a family and a size. Everything below — athlete cap, staff seats, AI credits, storage — comes from the selected plan. You can grow past the caps with paid extras.',
  planFamilyLbl:'Plan family', planSizeLbl:'Club size',
  planIncluded:'Included in this plan',
  planAthletes:'Athletes', planAiCredits:'AI credits / month', planStorage:'Storage',
  planMonthly:'monthly', planAnnual:'annual',
  planTeamTitle:'Team size', planTeamSub:'Go past the included caps with paid extras',
  planPerExtra:(v) => `${v} per extra`,
  planPriceBase:'Plan', planPriceAthletes:'Extra athletes', planPriceStaff:'Extra staff',
  planPriceTotal:'Monthly total',
  planLoading:'Loading plans...', planError:'Could not load the plans catalog', planRetry:'Retry',
  planReviewTitle:'Plan', planNone:'No plan selected',
  planExtraTag:(n) => `+${n} extra${n!==1?'s':''}`,
  planRoleLabels:{ coach_seats:'Coach', prep_seats:'S&C Coach', nutritionist_seats:'Nutritionist', manager_seats:'Manager' },
  planFamilyLabels:{ basic:'Basic', basic_plus:'Basic+', pro:'Pro', elite:'Elite' },
  s7Title:'Invite Team', s7Sub:'Send invites to coaches, athletes and staff',
  s7Info:'Guests will receive a link to create an account on the platform already linked to your club. You can invite more people later.',
  guestName:'Guest name', addInvite:'Add Invite',
  noInvites:'No invites added. You can invite people later in the management area.',
  s8Title:'Final Review', s8Sub:'Review your data before activating your club',
  s8Info:'Upon activation, your club will be visible on the platform and invites will be sent. You can edit everything later in the management area.',
  institutionalData:'Institutional Data', officialName:'Official Name',
  typeLabel:'Type', locationLabel:'Location', primarySportLabel:'Primary Sport',
  contactsSection:'Contacts', noContacts:'No contacts added', primaryLabel:'Primary',
  sportsStructure:'Sports Structure', noTeams:'No team added',
  athletesLabel:'athletes', coachesLabel:'coaches', noDetails:'Details not provided',
  invitesSection:'Invitations', noInvitesReview:'No invites — you can add later',
  headerTitle:'Club Setup', headerSub:'You are setting up the club as Manager. Complete all 8 steps to activate.',
  stepOf:(n) => `Step ${n} of 8`,
  loading:'Loading configuration...',
  clubActivated:'Club Activated!',
  clubActivatedSub:'Your organization is ready. See below what you can already do and what requires validation.',
  activeManager:'Active Manager', availableNow:'Available now',
  managementPanel:'Management Panel', viewFeed:'View Feed',
  requiresValidation:'Requires validation',
  requiresValidationSub:'These actions become available after the official club link is approved.',
  linkOfficialDataGated:'Link Official Data',
  manageOfficialAthletes:'Manage Official Athletes',
  fullInstitutionalPanel:'Full Institutional Panel',
  approveClubMembers:'Approve Club Members',
  requestOfficialLinkTitle:'Request the official link now',
  requestOfficialLinkDesc:'To access athletes and results, link your club to the official database.',
  back:'Back', next:'Next', activateClub:'Activate Club',
  saveExit:'Save and exit', discardSetup:'Cancel registration',
  discardTitle:'Cancel club registration?',
  discardBody:'All data entered in this setup will be permanently deleted. Your personal account will not be affected.',
  discardConfirm:'Yes, delete everything', discardKeep:'Keep setup',
  discarding:'Deleting...',
  selectPlaceholder:'Select...', catSelected:(n) => `${n} categor${n!==1?'ies':'y'} selected`,
  coachTag:'Coach', prepTag:'S&C',
  athleteCount2:(n) => `${n} athlete${n!==1?'s':''}`,
  managerLink:'Request Link',
} : {
  step1Label:'Instituição', step1Desc:'Dados do clube',
  step2Label:'Contatos', step2Desc:'Pessoas-chave',
  step3Label:'Estrutura', step3Desc:'Esportes e categorias',
  step4Label:'Equipe Técnica', step4Desc:'Treinadores e staff',
  step5Label:'Elenco', step5Desc:'Cadastrar atletas',
  step6Label:'Plano', step6Desc:'Plano e limites',
  step7Label:'Convites', step7Desc:'Convidar pessoas',
  step8Label:'Ativar', step8Desc:'Revisar e ativar',
  s1Title:'Dados do Clube', s1Sub:'Informações básicas da sua organização',
  s1Info:'Preencha os dados do seu clube para criar sua presença na plataforma. Campos com * são obrigatórios.',
  legalName:'Nome Oficial (Razão Social)', tradeName:'Nome Fantasia',
  orgType:'Tipo de Organização', country:'País', state:'Estado', city:'Cidade',
  primarySport:'Esporte Principal', primarySportSub:'Qual o principal esporte do clube?',
  s2Title:'Contatos-Chave', s2Sub:'Adicione as pessoas responsáveis pelo clube na plataforma',
  s2Info:'O Admin Master VUKZ será o primeiro administrador do sistema. Adicione pelo menos 1 contato para prosseguir.',
  roleLabel:'Função', fullName:'Nome Completo', jobTitle:'Cargo',
  emailLabel:'E-mail', phoneLabel:'Telefone', primaryContact:'Contato principal',
  removeContact:'Remover contato', addContact:'Adicionar Contato',
  s3Title:'Estrutura Esportiva', s3Sub:'Selecione os esportes e categorias que seu clube oferece',
  s3Info:'Para cada esporte, selecione todas as categorias de idade oferecidas. Você pode adicionar mais de um esporte se o clube for multi-esportivo.',
  offeredCats:'Categorias oferecidas', selectAll:'selecione todas',
  athleteCount:'Nº Atletas', coachCount:'Nº Treinadores', supportStaff:'Staff de Apoio',
  trainingVenue:'Local de Treino', ownFacility:'Instalação Própria', usesVideo:'Usa Vídeo',
  usesSensors:'Usa Sensores', usesSpreadsheets:'Usa Planilhas', currentSoftware:'Software Atual',
  addAnotherSport:'Adicionar Outro Esporte',
  linkOfficialData:'Vincular aos Dados Oficiais',
  linkOfficialDataDesc:'Para acessar os atletas e resultados do seu clube no banco de dados, você pode solicitar o vínculo oficial após concluir o cadastro.',
  requestLink:'Solicitar Vínculo',
  s4Title:'Equipe Técnica', s4Sub:'Cadastre treinadores, preparadores físicos e outros profissionais.',
  addProfessional:'Adicionar Profissional', noProfessionals:'Nenhum profissional cadastrado ainda.',
  clickAddProfessional:'Clique em "Adicionar Profissional" para começar.',
  editProfessional:'Editar Profissional', newProfessional:'Novo Profissional',
  fullNameLabel:'Nome completo', roleStarLabel:'Função *',
  assignedCategories:'Categorias atendidas',
  cancel:'Cancelar', save:'Salvar', add:'Adicionar', edit:'Editar',
  s5Title:'Elenco', s5Sub:'Cadastre os atletas e vincule a categorias e equipe técnica.',
  addAthlete:'Adicionar Atleta', allCategories:'Todas categorias',
  noAthletes:'Nenhum atleta cadastrado ainda.', athleteMinInfo:'Cadastro mínimo: nome, nascimento, sexo, categoria.',
  editAthlete:'Editar Atleta', newAthlete:'Novo Atleta',
  dob:'Data de nascimento', genderLabel:'Sexo *', male:'Masculino', female:'Feminino',
  categoryLabel:'Categoria *', mainCoach:'Treinador principal', scCoach:'Preparador(a) físico(a)', none:'Nenhum',
  s6Title:'Plano & Funcionalidades', s6Sub:'Funcionalidades e limites derivam do plano escolhido',
  s6Info:'Escolha família e tamanho. Tudo abaixo — limite de atletas, vagas de staff, créditos de IA, armazenamento — vem do plano selecionado. Você pode crescer além dos limites com extras pagos.',
  planFamilyLbl:'Família do plano', planSizeLbl:'Tamanho do clube',
  planIncluded:'Incluído neste plano',
  planAthletes:'Atletas', planAiCredits:'Créditos IA / mês', planStorage:'Armazenamento',
  planMonthly:'mensal', planAnnual:'anual',
  planTeamTitle:'Tamanho da equipe', planTeamSub:'Ultrapasse os limites incluídos com extras pagos',
  planPerExtra:(v) => `${v} por extra`,
  planPriceBase:'Plano', planPriceAthletes:'Atletas extras', planPriceStaff:'Staff extra',
  planPriceTotal:'Total mensal',
  planLoading:'Carregando planos...', planError:'Não foi possível carregar o catálogo de planos', planRetry:'Tentar novamente',
  planReviewTitle:'Plano', planNone:'Nenhum plano selecionado',
  planExtraTag:(n) => `+${n} extra${n!==1?'s':''}`,
  planRoleLabels:{ coach_seats:'Treinador(a)', prep_seats:'Preparador(a) Físico(a)', nutritionist_seats:'Nutricionista', manager_seats:'Gestor(a)' },
  planFamilyLabels:{ basic:'Basic', basic_plus:'Basic+', pro:'Pro', elite:'Elite' },
  s7Title:'Convidar Equipe', s7Sub:'Envie convites para treinadores, atletas e staff',
  s7Info:'Os convidados receberão um link para criar conta na plataforma já vinculados ao seu clube. Você pode convidar mais pessoas depois.',
  guestName:'Nome do convidado', addInvite:'Adicionar Convite',
  noInvites:'Nenhum convite adicionado. Você pode convidar pessoas depois na área de gestão.',
  s8Title:'Revisão Final', s8Sub:'Confira os dados antes de ativar seu clube na plataforma',
  s8Info:'Ao ativar, seu clube ficará visível na plataforma e os convites serão enviados. Você poderá editar tudo depois na área de gestão.',
  institutionalData:'Dados Institucionais', officialName:'Nome Oficial',
  typeLabel:'Tipo', locationLabel:'Localização', primarySportLabel:'Esporte Principal',
  contactsSection:'Contatos', noContacts:'Nenhum contato adicionado', primaryLabel:'Principal',
  sportsStructure:'Estrutura Esportiva', noTeams:'Nenhuma equipe adicionada',
  athletesLabel:'atletas', coachesLabel:'treinadores', noDetails:'Detalhes não informados',
  invitesSection:'Convites', noInvitesReview:'Nenhum convite — pode adicionar depois',
  headerTitle:'Configuração do Clube', headerSub:'Você está configurando o clube como Gestor. Complete os passos para ativar.',
  stepOf:(n) => `Passo ${n} de 8`,
  loading:'Carregando configuração...',
  clubActivated:'Clube Ativado!',
  clubActivatedSub:'Sua organização está pronta. Veja abaixo o que já pode fazer e o que requer validação.',
  activeManager:'Gestor Ativo', availableNow:'Disponível agora',
  managementPanel:'Painel de Gestão', viewFeed:'Ver o Feed',
  requiresValidation:'Requer validação',
  requiresValidationSub:'Estas ações ficam disponíveis após o vínculo oficial do clube ser aprovado.',
  linkOfficialDataGated:'Vincular Dados Oficiais',
  manageOfficialAthletes:'Gerenciar Atletas Oficiais',
  fullInstitutionalPanel:'Painel Institucional Completo',
  approveClubMembers:'Aprovar Membros do Clube',
  requestOfficialLinkTitle:'Solicite o vínculo oficial agora',
  requestOfficialLinkDesc:'Para acessar atletas e resultados, vincule seu clube ao banco de dados oficial.',
  back:'Anterior', next:'Próximo', activateClub:'Ativar Clube',
  saveExit:'Salvar e sair', discardSetup:'Cancelar cadastro',
  discardTitle:'Cancelar o cadastro do clube?',
  discardBody:'Todos os dados preenchidos nesta configuração serão apagados definitivamente. Sua conta pessoal não será afetada.',
  discardConfirm:'Sim, apagar tudo', discardKeep:'Manter configuração',
  discarding:'Apagando...',
  selectPlaceholder:'Selecione...', catSelected:(n) => `${n} categoria${n!==1?'s':''} selecionada${n!==1?'s':''}`,
  coachTag:'Téc.', prepTag:'Prep.',
  athleteCount2:(n) => `${n} atleta${n!==1?'s':''}`,
  managerLink:'Solicitar Vínculo',
};

/* ═══════════════════════════════════════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════════════════════════════════════ */

const getSteps = (t) => [
  { num: 1, label: t.step1Label, icon: Building2, desc: t.step1Desc },
  { num: 2, label: t.step2Label, icon: Users, desc: t.step2Desc },
  { num: 3, label: t.step3Label, icon: Dumbbell, desc: t.step3Desc },
  { num: 4, label: t.step4Label, icon: UserCog, desc: t.step4Desc },
  { num: 5, label: t.step5Label, icon: Users, desc: t.step5Desc },
  { num: 6, label: t.step6Label, icon: Package, desc: t.step6Desc },
  { num: 7, label: t.step7Label, icon: UserPlus, desc: t.step7Desc },
  { num: 8, label: t.step8Label, icon: CheckCircle2, desc: t.step8Desc },
];

const BRAZIL_STATES = [
  'AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT',
  'PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO',
];

/* ═══════════════════════════════════════════════════════════════════════════
   SHARED UI COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */

const PageShell = ({ children }) => (
  <div className="min-h-screen" style={{ backgroundColor: VUKZ.bg }}>
    <div className="max-w-5xl mx-auto px-4 lg:px-6 py-6">
      {children}
    </div>
  </div>
);

const SectionTitle = ({ icon: Icon, title, subtitle }) => (
  <div className="mb-6">
    <div className="flex items-center gap-3 mb-1">
      {Icon && <Icon size={22} style={{ color: VUKZ.primary }} />}
      <h2 className="text-xl font-bold" style={{ color: VUKZ.text }}>{title}</h2>
    </div>
    {subtitle && <p className="text-sm ml-9" style={{ color: VUKZ.textLight }}>{subtitle}</p>}
  </div>
);

const InputField = ({ label, value, onChange, placeholder, type = 'text', required, icon: Icon, ...rest }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium mb-1.5" style={{ color: VUKZ.text }}>
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      {Icon && (
        <div className="absolute left-3 top-1/2 -translate-y-1/2">
          <Icon size={16} style={{ color: VUKZ.textLight }} />
        </div>
      )}
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 transition ${Icon ? 'pl-10' : ''}`}
        style={{ borderColor: VUKZ.border, color: VUKZ.text }}
        {...rest}
      />
    </div>
  </div>
);

const SelectField = ({ label, value, onChange, options, placeholder, required }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium mb-1.5" style={{ color: VUKZ.text }}>
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 transition appearance-none bg-white"
      style={{ borderColor: VUKZ.border, color: value ? VUKZ.text : VUKZ.textLight }}
    >
      <option value="">{placeholder || 'Selecione...'}</option>
      {options.map(o => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </div>
);

const InfoBox = ({ children }) => (
  <div className="flex items-start gap-3 p-4 rounded-xl mb-6"
    style={{ backgroundColor: '#f9fafb', border: `1px solid ${VUKZ.border}` }}>
    <Info size={18} className="mt-0.5 flex-shrink-0" style={{ color: VUKZ.textLight }} />
    <div className="text-sm" style={{ color: VUKZ.textLight }}>{children}</div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   STEP 1 — DADOS INSTITUCIONAIS
   ═══════════════════════════════════════════════════════════════════════════ */

const Step1Institution = ({ formData, setFormData }) => {
  const t = useWizardT();
  const update = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));

  return (
    <div>
      <SectionTitle icon={Building2} title={t.s1Title} subtitle={t.s1Sub} />

      <InfoBox>{t.s1Info}</InfoBox>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
        <InputField label={t.legalName} value={formData.legal_name}
          onChange={v => update('legal_name', v)} placeholder="Ex: Minas Tênis Clube" required />
        <InputField label={t.tradeName} value={formData.trade_name}
          onChange={v => update('trade_name', v)} placeholder="Ex: Minas Natação" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
        <SelectField label={t.orgType} value={formData.org_type}
          onChange={v => update('org_type', v)} options={ORG_TYPES} required />
        <InputField label="CNPJ" value={formData.cnpj}
          onChange={v => update('cnpj', v)} placeholder="00.000.000/0001-00" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6">
        <SelectField label={t.country} value={formData.country}
          onChange={v => update('country', v)}
          options={[
            { value: 'Brasil', label: 'Brasil' },
            { value: 'Portugal', label: 'Portugal' },
            { value: 'USA', label: 'EUA' },
            { value: 'Outro', label: 'Outro' },
          ]} required />
        <SelectField label={t.state} value={formData.state}
          onChange={v => update('state', v)}
          options={BRAZIL_STATES.map(s => ({ value: s, label: s }))} required />
        <InputField label={t.city} value={formData.city}
          onChange={v => update('city', v)} placeholder="Ex: Belo Horizonte" icon={MapPin} required />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
        <InputField label="Website" value={formData.website}
          onChange={v => update('website', v)} placeholder="https://..." icon={Globe} />
        <InputField label="Instagram" value={formData.instagram}
          onChange={v => update('instagram', v)} placeholder="@seuclube" icon={Instagram} />
      </div>

      <SectionTitle icon={Dumbbell} title={t.primarySport} subtitle={t.primarySportSub} />

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
        {SPORT_OPTIONS.filter(s => s.enabled).map(s => (
          <button key={s.value}
            onClick={() => update('primary_sport', s.value)}
            className="p-3 rounded-xl border-2 text-center transition-all hover:shadow-md"
            style={{
              borderColor: formData.primary_sport === s.value ? VUKZ.primary : VUKZ.border,
              backgroundColor: formData.primary_sport === s.value ? '#f3f4f6' : 'white',
            }}>
            <div className="text-sm font-medium" style={{ color: VUKZ.text }}>{s.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   STEP 2 — CONTATOS-CHAVE
   ═══════════════════════════════════════════════════════════════════════════ */

const emptyContact = { contact_role: '', full_name: '', title: '', email: '', phone: '', is_primary: false };

const Step2Contacts = ({ contacts, setContacts }) => {
  const t = useWizardT();
  const addContact = () => setContacts(prev => [...prev, { ...emptyContact }]);
  const removeContact = (idx) => setContacts(prev => prev.filter((_, i) => i !== idx));
  const updateContact = (idx, key, val) => {
    setContacts(prev => prev.map((c, i) => i === idx ? { ...c, [key]: val } : c));
  };

  return (
    <div>
      <SectionTitle icon={Users} title={t.s2Title} subtitle={t.s2Sub} />

      <InfoBox>{t.s2Info}</InfoBox>

      {contacts.map((c, idx) => (
        <div key={idx} className="bg-white rounded-xl border p-5 mb-4 relative"
          style={{ borderColor: VUKZ.border }}>
          {contacts.length > 1 && (
            <button onClick={() => removeContact(idx)}
              className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-red-50 transition"
              title={t.removeContact}>
              <Trash2 size={16} className="text-red-400" />
            </button>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <SelectField label={t.roleLabel} value={c.contact_role}
              onChange={v => updateContact(idx, 'contact_role', v)} options={CONTACT_ROLES} required />
            <InputField label={t.fullName} value={c.full_name}
              onChange={v => updateContact(idx, 'full_name', v)} placeholder={t.fullName} required />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6">
            <InputField label={t.jobTitle} value={c.title}
              onChange={v => updateContact(idx, 'title', v)} placeholder="Ex: Diretor Técnico" />
            <InputField label={t.emailLabel} value={c.email} type="email" icon={Mail}
              onChange={v => updateContact(idx, 'email', v)} placeholder="email@clube.com" />
            <InputField label={t.phoneLabel} value={c.phone} icon={Phone}
              onChange={v => updateContact(idx, 'phone', v)} placeholder="(31) 99999-0000" />
          </div>

          <label className="flex items-center gap-2 cursor-pointer mt-1">
            <input type="checkbox" checked={c.is_primary}
              onChange={(e) => updateContact(idx, 'is_primary', e.target.checked)}
              className="w-4 h-4 rounded border-gray-300" />
            <span className="text-sm" style={{ color: VUKZ.textLight }}>{t.primaryContact}</span>
          </label>
        </div>
      ))}

      <button onClick={addContact}
        className="w-full py-3 border-2 border-dashed rounded-xl text-sm font-medium flex items-center justify-center gap-2 hover:bg-gray-50 transition"
        style={{ borderColor: VUKZ.border, color: VUKZ.textLight }}>
        <Plus size={16} /> {t.addContact}
      </button>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   STEP 3 — ESTRUTURA ESPORTIVA
   ═══════════════════════════════════════════════════════════════════════════ */

const emptySport = {
  sport_code: 'swimming', categories: [],
  athlete_count: '', coach_count: '', support_staff_count: '',
  training_location: '', has_own_facility: false,
  uses_video: false, uses_sensors: false, uses_spreadsheets: false,
  current_software: '',
};

const Step3Sports = ({ sports, setSports, primarySport, institutionId }) => {
  const t = useWizardT();
  const addSport = () => setSports(prev => [...prev, { ...emptySport, sport_code: primarySport || 'swimming' }]);
  const removeSport = (idx) => setSports(prev => prev.filter((_, i) => i !== idx));
  const updateSport = (idx, key, val) => {
    setSports(prev => prev.map((s, i) => i === idx ? { ...s, [key]: val } : s));
  };
  const toggleCategory = (idx, catValue) => {
    setSports(prev => prev.map((s, i) => {
      if (i !== idx) return s;
      const cats = s.categories || [];
      return {
        ...s,
        categories: cats.includes(catValue)
          ? cats.filter(c => c !== catValue)
          : [...cats, catValue],
      };
    }));
  };

  return (
    <div>
      <SectionTitle icon={Dumbbell} title={t.s3Title} subtitle={t.s3Sub} />

      <InfoBox>{t.s3Info}</InfoBox>

      {sports.map((s, idx) => (
        <div key={idx} className="bg-white rounded-xl border p-5 mb-4 relative"
          style={{ borderColor: VUKZ.border }}>
          {sports.length > 1 && (
            <button onClick={() => removeSport(idx)}
              className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-red-50 transition">
              <Trash2 size={16} className="text-red-400" />
            </button>
          )}

          {/* Esporte */}
          <SelectField label={t.step3Label} value={s.sport_code}
            onChange={v => updateSport(idx, 'sport_code', v)}
            options={SPORT_OPTIONS.filter(sp => sp.enabled)} required />

          {/* Categorias — Multi-select com checkboxes */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2" style={{ color: VUKZ.text }}>
              {t.offeredCats} <span className="text-xs font-normal" style={{ color: VUKZ.textLight }}>({t.selectAll})</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {CATEGORY_OPTIONS.map(cat => {
                const isSelected = (s.categories || []).includes(cat.value);
                return (
                  <button key={cat.value} type="button"
                    onClick={() => toggleCategory(idx, cat.value)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all text-left"
                    style={{
                      borderColor: isSelected ? VUKZ.primary : VUKZ.border,
                      backgroundColor: isSelected ? '#f0fdf4' : '#fff',
                      color: isSelected ? VUKZ.primary : VUKZ.text,
                      fontWeight: isSelected ? 600 : 400,
                    }}>
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all`}
                      style={{
                        borderColor: isSelected ? VUKZ.primary : '#d1d5db',
                        backgroundColor: isSelected ? VUKZ.primary : 'transparent',
                      }}>
                      {isSelected && <Check size={10} className="text-white" />}
                    </div>
                    {cat.label}
                  </button>
                );
              })}
            </div>
            {(s.categories || []).length > 0 && (
              <p className="text-xs mt-2" style={{ color: VUKZ.primary }}>
                {t.catSelected((s.categories || []).length)}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6">
            <InputField label={t.athleteCount} value={s.athlete_count} type="number"
              onChange={v => updateSport(idx, 'athlete_count', v)} placeholder="0" />
            <InputField label={t.coachCount} value={s.coach_count} type="number"
              onChange={v => updateSport(idx, 'coach_count', v)} placeholder="0" />
            <InputField label={t.supportStaff} value={s.support_staff_count} type="number"
              onChange={v => updateSport(idx, 'support_staff_count', v)} placeholder="0" />
            <InputField label={t.trainingVenue} value={s.training_location}
              onChange={v => updateSport(idx, 'training_location', v)} placeholder="Ex: Piscina do Minas" />
          </div>

          <div className="flex flex-wrap gap-4 mt-3">
            {[
              { key: 'has_own_facility', label: t.ownFacility },
              { key: 'uses_video', label: t.usesVideo },
              { key: 'uses_sensors', label: t.usesSensors },
              { key: 'uses_spreadsheets', label: t.usesSpreadsheets },
            ].map(opt => (
              <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={s[opt.key] || false}
                  onChange={(e) => updateSport(idx, opt.key, e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300" />
                <span className="text-sm" style={{ color: VUKZ.textLight }}>{opt.label}</span>
              </label>
            ))}
          </div>

          <div className="mt-3">
            <InputField label={t.currentSoftware} value={s.current_software}
              onChange={v => updateSport(idx, 'current_software', v)}
              placeholder="Ex: Planilha Excel, sistema próprio, nenhum..." />
          </div>
        </div>
      ))}

      <button onClick={addSport}
        className="w-full py-3 border-2 border-dashed rounded-xl text-sm font-medium flex items-center justify-center gap-2 hover:bg-gray-50 transition"
        style={{ borderColor: VUKZ.border, color: VUKZ.textLight }}>
        <Plus size={16} /> {t.addAnotherSport}
      </button>

      {/* ── Vincular Dados Oficiais ──────────────────────────────────── */}
      <div className="mt-6 p-4 rounded-xl border-2 border-dashed"
        style={{ borderColor: VUKZ.gold || '#b48c2c', backgroundColor: '#fffbeb' }}>
        <div className="flex items-start gap-3">
          <Search size={20} className="mt-0.5 flex-shrink-0" style={{ color: VUKZ.gold || '#b48c2c' }} />
          <div className="flex-1">
            <h4 className="text-sm font-bold mb-1" style={{ color: VUKZ.text }}>
              {t.linkOfficialData}
            </h4>
            <p className="text-xs mb-3" style={{ color: VUKZ.textLight }}>
              {t.linkOfficialDataDesc}
            </p>
            {/* Gate fechado durante o wizard — o próprio desc acima promete
                "após concluir o cadastro"; o botão obedece (C1). O CTA vivo
                equivalente mora na tela completed (Step 8), que é gated lá. */}
            <GatedAction enabled={false} icon={Search} variant="gold">
              {t.requestLink}
            </GatedAction>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   STEP 4 — EQUIPE TÉCNICA (NOVO — Migration 166)
   ═══════════════════════════════════════════════════════════════════════════ */

const STAFF_ROLES = [
  { code: 'TREINADOR', label: 'Treinador(a)' },
  { code: 'PREPARADOR_FISICO', label: 'Preparador(a) Físico(a)' },
  { code: 'FISIOTERAPEUTA', label: 'Fisioterapeuta' },
  { code: 'NUTRICIONISTA', label: 'Nutricionista' },
  { code: 'MEDICO', label: 'Médico(a)' },
  { code: 'ANALISTA', label: 'Analista' },
];

const Step4Staff = ({ staffMembers, setStaffMembers, categories }) => {
  const t = useWizardT();
  const [showModal, setShowModal] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const emptyStaff = { full_name: '', email: '', phone: '', staff_role: 'TREINADOR', category_ids: [] };
  const [form, setForm] = useState(emptyStaff);

  const openAdd = () => { setForm(emptyStaff); setEditingIdx(null); setShowModal(true); };
  const openEdit = (idx) => { setForm({ ...staffMembers[idx] }); setEditingIdx(idx); setShowModal(true); };
  const handleSave = () => {
    if (!form.full_name.trim()) return;
    if (editingIdx !== null) {
      setStaffMembers(prev => prev.map((s, i) => i === editingIdx ? { ...form } : s));
    } else {
      setStaffMembers(prev => [...prev, { ...form }]);
    }
    setShowModal(false);
  };
  const handleRemove = (idx) => setStaffMembers(prev => prev.filter((_, i) => i !== idx));

  const roleLabel = (code) => STAFF_ROLES.find(r => r.code === code)?.label || code;
  const catLabel = (id) => categories.find(c => c.category_id === id || c._tempId === id)?.category_code || `#${id}`;

  return (
    <div>
      <SectionTitle icon={Users} title={t.s4Title} subtitle={t.s4Sub} />

      <button onClick={openAdd}
        className="mb-4 flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm border-2 border-dashed transition hover:bg-gray-50"
        style={{ borderColor: VUKZ.border, color: VUKZ.primary }}>
        <Plus size={16} /> {t.addProfessional}
      </button>

      {staffMembers.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border" style={{ borderColor: VUKZ.border }}>
          <Users size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm" style={{ color: VUKZ.textLight }}>{t.noProfessionals}</p>
          <p className="text-xs mt-1" style={{ color: VUKZ.textLight }}>{t.clickAddProfessional}</p>
        </div>
      )}

      <div className="space-y-3">
        {staffMembers.map((s, i) => (
          <div key={i} className="bg-white rounded-xl border p-4 flex items-center justify-between"
            style={{ borderColor: VUKZ.border }}>
            <div className="flex items-center gap-3">
              <div>
                <h4 className="font-bold text-sm" style={{ color: VUKZ.text }}>{s.full_name}</h4>
                <p className="text-xs" style={{ color: VUKZ.textLight }}>
                  {roleLabel(s.staff_role)}
                  {s.email && ` • ${s.email}`}
                </p>
                {s.category_ids?.length > 0 && (
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {s.category_ids.map(id => (
                      <span key={id} className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100" style={{ color: VUKZ.textLight }}>
                        {catLabel(id)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => openEdit(i)} className="text-xs px-3 py-1.5 border rounded-lg hover:bg-gray-50" style={{ borderColor: VUKZ.border }}>{t.edit}</button>
              <button onClick={() => handleRemove(i)} className="text-xs px-3 py-1.5 border rounded-lg hover:bg-red-50 text-red-500" style={{ borderColor: '#fecaca' }}>
                <Trash2 size={12} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4" style={{ color: VUKZ.text }}>
              {editingIdx !== null ? t.editProfessional : t.newProfessional}
            </h3>

            <InputField label={t.fullNameLabel} value={form.full_name} required
              onChange={v => setForm(f => ({ ...f, full_name: v }))} placeholder="Ex: Carlos Silva" />

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1.5" style={{ color: VUKZ.text }}>{t.roleStarLabel}</label>
              <select value={form.staff_role} onChange={e => setForm(f => ({ ...f, staff_role: e.target.value }))}
                className="w-full px-3 py-2.5 border rounded-lg text-sm" style={{ borderColor: VUKZ.border }}>
                {STAFF_ROLES.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
              </select>
            </div>

            <InputField label="Email" value={form.email} type="email"
              onChange={v => setForm(f => ({ ...f, email: v }))} placeholder="carlos@clube.com" icon={Mail} />
            <InputField label="Telefone" value={form.phone}
              onChange={v => setForm(f => ({ ...f, phone: v }))} placeholder="(11) 99999-0000" icon={Phone} />

            {categories.length > 0 && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1.5" style={{ color: VUKZ.text }}>{t.assignedCategories}</label>
                <div className="flex flex-wrap gap-2">
                  {categories.map(cat => {
                    const id = cat.category_id || cat._tempId;
                    const selected = form.category_ids?.includes(id);
                    return (
                      <button key={id} type="button"
                        onClick={() => setForm(f => ({
                          ...f,
                          category_ids: selected
                            ? f.category_ids.filter(c => c !== id)
                            : [...(f.category_ids || []), id]
                        }))}
                        className={`text-xs px-3 py-1.5 rounded-full border transition ${selected ? 'font-bold' : ''}`}
                        style={{
                          backgroundColor: selected ? VUKZ.primary : 'white',
                          color: selected ? 'white' : VUKZ.text,
                          borderColor: selected ? VUKZ.primary : VUKZ.border,
                        }}>
                        {cat.category_code || cat.category_name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowModal(false)}
                className="flex-1 py-2.5 border rounded-lg text-sm font-medium" style={{ borderColor: VUKZ.border }}>
                {t.cancel}
              </button>
              <button onClick={handleSave}
                disabled={!form.full_name.trim()}
                className="flex-1 py-2.5 rounded-lg text-sm font-bold text-white transition disabled:opacity-40"
                style={{ backgroundColor: VUKZ.primary }}>
                {editingIdx !== null ? t.save : t.add}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   STEP 5 — ELENCO (NOVO — Migration 166)
   ═══════════════════════════════════════════════════════════════════════════ */

const Step5Roster = ({ athletes, setAthletes, categories, staffMembers }) => {
  const t = useWizardT();
  const [showModal, setShowModal] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [filterCat, setFilterCat] = useState('all');

  const emptyAthlete = { full_name: '', birth_date: '', gender: '', category_id: null, coach_id: null, prep_id: null };
  const [form, setForm] = useState(emptyAthlete);

  const openAdd = () => { setForm(emptyAthlete); setEditingIdx(null); setShowModal(true); };
  const openEdit = (idx) => { setForm({ ...athletes[idx] }); setEditingIdx(idx); setShowModal(true); };
  const handleSave = () => {
    if (!form.full_name.trim()) return;
    if (editingIdx !== null) {
      setAthletes(prev => prev.map((a, i) => i === editingIdx ? { ...form } : a));
    } else {
      setAthletes(prev => [...prev, { ...form }]);
    }
    setShowModal(false);
  };
  const handleRemove = (idx) => setAthletes(prev => prev.filter((_, i) => i !== idx));

  const catLabel = (id) => categories.find(c => (c.category_id || c._tempId) === id)?.category_code || '';
  const staffName = (idx) => staffMembers[idx]?.full_name || '';
  const coaches = staffMembers.filter(s => s.staff_role === 'TREINADOR').map((s, i) => ({ ...s, _idx: staffMembers.indexOf(s) }));
  const preps = staffMembers.filter(s => s.staff_role === 'PREPARADOR_FISICO').map((s, i) => ({ ...s, _idx: staffMembers.indexOf(s) }));

  const calcAge = (bd) => {
    if (!bd) return null;
    const d = new Date(bd);
    const diff = Date.now() - d.getTime();
    return Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
  };

  const filtered = filterCat === 'all' ? athletes : athletes.filter(a => a.category_id === filterCat);

  return (
    <div>
      <SectionTitle icon={Dumbbell} title={t.s5Title} subtitle={t.s5Sub} />

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <button onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm border-2 border-dashed transition hover:bg-gray-50"
          style={{ borderColor: VUKZ.border, color: VUKZ.primary }}>
          <Plus size={16} /> {t.addAthlete}
        </button>

        {categories.length > 0 && (
          <select value={filterCat} onChange={e => setFilterCat(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: VUKZ.border }}>
            <option value="all">{t.allCategories}</option>
            {categories.map(c => (
              <option key={c.category_id || c._tempId} value={c.category_id || c._tempId}>
                {c.category_code}
              </option>
            ))}
          </select>
        )}

        <span className="text-xs ml-auto" style={{ color: VUKZ.textLight }}>
          {t.athleteCount2(filtered.length)}
        </span>
      </div>

      {athletes.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border" style={{ borderColor: VUKZ.border }}>
          <Dumbbell size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm" style={{ color: VUKZ.textLight }}>{t.noAthletes}</p>
          <p className="text-xs mt-1" style={{ color: VUKZ.textLight }}>{t.athleteMinInfo}</p>
        </div>
      )}

      <div className="space-y-2">
        {filtered.map((a, i) => {
          const realIdx = athletes.indexOf(a);
          const age = calcAge(a.birth_date);
          return (
            <div key={realIdx} className="bg-white rounded-xl border p-4 flex items-center justify-between"
              style={{ borderColor: VUKZ.border }}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-bold text-sm truncate" style={{ color: VUKZ.text }}>{a.full_name}</h4>
                  {age && <span className="text-xs px-2 py-0.5 bg-gray-100 rounded-full" style={{ color: VUKZ.textLight }}>{age} anos</span>}
                  {a.gender && <span className="text-xs" style={{ color: VUKZ.textLight }}>{a.gender === 'male' ? 'M' : 'F'}</span>}
                </div>
                <div className="flex gap-3 mt-1 text-xs" style={{ color: VUKZ.textLight }}>
                  {a.category_id && <span>{catLabel(a.category_id)}</span>}
                  {a.coach_id !== null && a.coach_id !== undefined && <span>{t.coachTag} {staffName(a.coach_id)}</span>}
                  {a.prep_id !== null && a.prep_id !== undefined && <span>{t.prepTag} {staffName(a.prep_id)}</span>}
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => openEdit(realIdx)} className="text-xs px-3 py-1.5 border rounded-lg hover:bg-gray-50" style={{ borderColor: VUKZ.border }}>{t.edit}</button>
                <button onClick={() => handleRemove(realIdx)} className="text-xs px-3 py-1.5 border rounded-lg hover:bg-red-50 text-red-500" style={{ borderColor: '#fecaca' }}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4" style={{ color: VUKZ.text }}>
              {editingIdx !== null ? t.editAthlete : t.newAthlete}
            </h3>

            <InputField label={t.fullNameLabel} value={form.full_name} required
              onChange={v => setForm(f => ({ ...f, full_name: v }))} placeholder="Ex: João Mendes" />

            <InputField label={t.dob} value={form.birth_date} type="date" required
              onChange={v => setForm(f => ({ ...f, birth_date: v }))} />

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1.5" style={{ color: VUKZ.text }}>{t.genderLabel}</label>
              <div className="flex gap-3">
                {[{ v: 'male', l: t.male }, { v: 'female', l: t.female }].map(o => (
                  <button key={o.v} type="button"
                    onClick={() => setForm(f => ({ ...f, gender: o.v }))}
                    className={`flex-1 py-2.5 rounded-lg border text-sm font-medium transition ${form.gender === o.v ? 'ring-2 ring-gray-900' : ''}`}
                    style={{
                      backgroundColor: form.gender === o.v ? VUKZ.primary : 'white',
                      color: form.gender === o.v ? 'white' : VUKZ.text,
                      borderColor: form.gender === o.v ? VUKZ.primary : VUKZ.border,
                    }}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>

            {categories.length > 0 && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1.5" style={{ color: VUKZ.text }}>{t.categoryLabel}</label>
                <select value={form.category_id || ''} onChange={e => setForm(f => ({ ...f, category_id: Number(e.target.value) }))}
                  className="w-full px-3 py-2.5 border rounded-lg text-sm" style={{ borderColor: VUKZ.border }}>
                  <option value="">Selecione...</option>
                  {categories.map(c => (
                    <option key={c.category_id || c._tempId} value={c.category_id || c._tempId}>
                      {c.category_code}{c.category_name ? ` — ${c.category_name}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {coaches.length > 0 && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1.5" style={{ color: VUKZ.text }}>{t.mainCoach}</label>
                <select value={form.coach_id ?? ''} onChange={e => setForm(f => ({ ...f, coach_id: e.target.value ? Number(e.target.value) : null }))}
                  className="w-full px-3 py-2.5 border rounded-lg text-sm" style={{ borderColor: VUKZ.border }}>
                  <option value="">{t.none}</option>
                  {coaches.map(s => (
                    <option key={s._idx} value={s._idx}>{s.full_name}</option>
                  ))}
                </select>
              </div>
            )}

            {preps.length > 0 && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1.5" style={{ color: VUKZ.text }}>{t.scCoach}</label>
                <select value={form.prep_id ?? ''} onChange={e => setForm(f => ({ ...f, prep_id: e.target.value ? Number(e.target.value) : null }))}
                  className="w-full px-3 py-2.5 border rounded-lg text-sm" style={{ borderColor: VUKZ.border }}>
                  <option value="">{t.none}</option>
                  {preps.map(s => (
                    <option key={s._idx} value={s._idx}>{s.full_name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowModal(false)}
                className="flex-1 py-2.5 border rounded-lg text-sm font-medium" style={{ borderColor: VUKZ.border }}>
                {t.cancel}
              </button>
              <button onClick={handleSave}
                disabled={!form.full_name.trim()}
                className="flex-1 py-2.5 rounded-lg text-sm font-bold text-white transition disabled:opacity-40"
                style={{ backgroundColor: VUKZ.primary }}>
                {editingIdx !== null ? t.save : t.add}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   STEP 6 — PLAN & FEATURES (data-driven: GET /billing/public/plans)
   Grade 16 células (família × porte) + extras à la carte POR CIMA da célula
   (decisão 07/07/2026). Fonte única = payload; nada hardcoded aqui.
   Gate dos steppers de staff: role_seats > 0 (vem do banco).
   ═══════════════════════════════════════════════════════════════════════════ */

const FAMILY_ORDER = ['basic', 'basic_plus', 'pro', 'elite'];
const BAND_ORDER = ['S', 'M', 'L', 'XL'];

const PlanStepper = ({ label, sub, value, min, onDec, onInc }) => (
  <div className="flex items-center justify-between px-5 py-3 border-b last:border-b-0"
    style={{ borderColor: VUKZ.border }}>
    <div>
      <div className="text-sm font-medium" style={{ color: VUKZ.text }}>{label}</div>
      {sub && <div className="text-xs" style={{ color: VUKZ.textLight }}>{sub}</div>}
    </div>
    <div className="flex items-center gap-2">
      <button type="button" onClick={onDec} disabled={value <= min}
        className="w-8 h-8 rounded-lg border flex items-center justify-center transition disabled:opacity-30 hover:bg-gray-50"
        style={{ borderColor: VUKZ.border, color: VUKZ.text }}>
        <Minus size={14} />
      </button>
      <span className="w-10 text-center text-sm font-semibold tabular-nums" style={{ color: VUKZ.text }}>
        {value}
      </span>
      <button type="button" onClick={onInc}
        className="w-8 h-8 rounded-lg border flex items-center justify-center transition hover:bg-gray-50"
        style={{ borderColor: VUKZ.border, color: VUKZ.text }}>
        <Plus size={14} />
      </button>
    </div>
  </div>
);

const StepPlan = ({ planSelection, setPlanSelection }) => {
  const t = useWizardT();
  const [plans, setPlans] = useState(null);
  const [loadErr, setLoadErr] = useState(false);

  const load = useCallback(async () => {
    setLoadErr(false);
    try {
      setPlans(await getPublicPlans());
    } catch (e) {
      console.error('[StepPlan] catalog load failed:', e);
      setLoadErr(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const families = useMemo(() => {
    if (!plans) return [];
    const present = new Set(plans.map(p => p.plan_family));
    return FAMILY_ORDER.filter(f => present.has(f));
  }, [plans]);

  const bands = useMemo(() => {
    if (!plans) return [];
    const present = new Set(plans.map(p => p.size_band));
    return BAND_ORDER.filter(b => present.has(b));
  }, [plans]);

  const cell = planSelection.cellData || null;

  const pick = (family, band) => {
    const f = family ?? planSelection.family;
    const b = band ?? planSelection.band;
    const c = (plans || []).find(p => p.plan_family === f && p.size_band === b) || null;
    setPlanSelection({
      family: f,
      band: b,
      cell: c?.plan_key || null,
      cellData: c,
      athletes: c ? c.athlete_limit : null,
      staffCounts: c
        ? Object.fromEntries(Object.entries(c.role_seats || {}).filter(([, v]) => v > 0))
        : {},
    });
  };

  const bumpAthletes = (delta) => {
    if (!cell) return;
    setPlanSelection(prev => ({
      ...prev,
      athletes: Math.max(cell.athlete_limit, (prev.athletes ?? cell.athlete_limit) + delta),
    }));
  };

  const bumpStaff = (roleKey, delta) => {
    if (!cell) return;
    const seats = cell.role_seats?.[roleKey] || 0;
    setPlanSelection(prev => ({
      ...prev,
      staffCounts: {
        ...prev.staffCounts,
        [roleKey]: Math.max(seats, (prev.staffCounts?.[roleKey] ?? seats) + delta),
      },
    }));
  };

  // Extras à la carte por cima da célula (preço marginal do payload)
  const breakdown = useMemo(() => {
    if (!cell) return null;
    const extraAthletes = Math.max(0, (planSelection.athletes ?? cell.athlete_limit) - cell.athlete_limit);
    const extraStaff = Object.entries(cell.role_seats || {})
      .filter(([, seats]) => seats > 0)
      .reduce((sum, [k, seats]) => sum + Math.max(0, (planSelection.staffCounts?.[k] ?? seats) - seats), 0);
    const athletesCents = extraAthletes * (cell.price_per_athlete_cents || 0);
    const staffCents = extraStaff * (cell.price_per_staff_cents || 0);
    return {
      extraAthletes, extraStaff, athletesCents, staffCents,
      totalCents: (cell.price_brl_cents || 0) + athletesCents + staffCents,
    };
  }, [cell, planSelection.athletes, planSelection.staffCounts]);

  const storageLabel = (mb) => (mb >= 1024 ? `${Math.round(mb / 1024)} GB` : `${mb} MB`);

  if (loadErr) {
    return (
      <div>
        <SectionTitle icon={Package} title={t.s6Title} subtitle={t.s6Sub} />
        <div className="bg-white rounded-xl border p-8 text-center" style={{ borderColor: VUKZ.border }}>
          <AlertCircle size={24} className="mx-auto mb-3" style={{ color: VUKZ.textLight }} />
          <p className="text-sm mb-4" style={{ color: VUKZ.text }}>{t.planError}</p>
          <button onClick={load}
            className="px-4 py-2 rounded-lg text-sm font-semibold border hover:bg-gray-50 transition"
            style={{ borderColor: VUKZ.border, color: VUKZ.text }}>
            {t.planRetry}
          </button>
        </div>
      </div>
    );
  }

  if (!plans) {
    return (
      <div>
        <SectionTitle icon={Package} title={t.s6Title} subtitle={t.s6Sub} />
        <div className="bg-white rounded-xl border p-8 flex items-center justify-center gap-3"
          style={{ borderColor: VUKZ.border }}>
          <Loader2 size={18} className="animate-spin" style={{ color: VUKZ.textLight }} />
          <span className="text-sm" style={{ color: VUKZ.textLight }}>{t.planLoading}</span>
        </div>
      </div>
    );
  }

  const seatRoles = cell
    ? Object.entries(cell.role_seats || {}).filter(([, seats]) => seats > 0)
    : [];

  return (
    <div>
      <SectionTitle icon={Package} title={t.s6Title} subtitle={t.s6Sub} />

      <InfoBox>{t.s6Info}</InfoBox>

      {/* Family picker */}
      <div className="mb-4">
        <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: VUKZ.textLight }}>
          {t.planFamilyLbl}
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {families.map(f => {
            const active = planSelection.family === f;
            return (
              <button key={f} type="button" onClick={() => pick(f, undefined)}
                className={`px-3 py-2.5 rounded-lg border text-sm font-semibold transition ${
                  active ? 'bg-gray-900 text-white border-gray-900' : 'bg-white hover:bg-gray-50'
                }`}
                style={active ? {} : { borderColor: VUKZ.border, color: VUKZ.text }}>
                {t.planFamilyLabels[f] || f}
              </button>
            );
          })}
        </div>
      </div>

      {/* Band picker */}
      <div className="mb-6">
        <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: VUKZ.textLight }}>
          {t.planSizeLbl}
        </label>
        <div className="grid grid-cols-4 gap-2">
          {bands.map(b => {
            const active = planSelection.band === b;
            const hint = planSelection.family
              ? plans.find(p => p.plan_family === planSelection.family && p.size_band === b)?.athlete_limit
              : null;
            return (
              <button key={b} type="button" onClick={() => pick(undefined, b)}
                className={`px-3 py-2.5 rounded-lg border text-sm font-semibold transition ${
                  active ? 'bg-gray-900 text-white border-gray-900' : 'bg-white hover:bg-gray-50'
                }`}
                style={active ? {} : { borderColor: VUKZ.border, color: VUKZ.text }}>
                <div>{b}</div>
                {hint != null && (
                  <div className={`text-xs font-normal ${active ? 'opacity-70' : ''}`}
                    style={active ? {} : { color: VUKZ.textLight }}>
                    {hint} {t.planAthletes.toLowerCase()}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {cell && (
        <>
          {/* Included in this plan — tudo derivado da célula */}
          <div className="bg-white rounded-xl border overflow-hidden mb-6" style={{ borderColor: VUKZ.border }}>
            <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: VUKZ.border }}>
              <span className="text-sm font-semibold" style={{ color: VUKZ.text }}>{t.planIncluded}</span>
              <div className="text-right">
                <div className="text-sm font-bold" style={{ color: VUKZ.text }}>
                  {formatBRL(cell.price_brl_cents)}
                  <span className="text-xs font-normal" style={{ color: VUKZ.textLight }}> / {t.planMonthly}</span>
                </div>
                {cell.annual_price_cents != null && (
                  <div className="text-xs" style={{ color: VUKZ.textLight }}>
                    {formatBRL(cell.annual_price_cents)} / {t.planAnnual}
                  </div>
                )}
              </div>
            </div>
            <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <div className="text-lg font-bold" style={{ color: VUKZ.text }}>{cell.athlete_limit}</div>
                <div className="text-xs" style={{ color: VUKZ.textLight }}>{t.planAthletes}</div>
              </div>
              {seatRoles.map(([k, seats]) => (
                <div key={k}>
                  <div className="text-lg font-bold" style={{ color: VUKZ.text }}>{seats}</div>
                  <div className="text-xs" style={{ color: VUKZ.textLight }}>{t.planRoleLabels[k] || k}</div>
                </div>
              ))}
              <div>
                <div className="text-lg font-bold" style={{ color: VUKZ.text }}>{cell.ai_credits_monthly}</div>
                <div className="text-xs" style={{ color: VUKZ.textLight }}>{t.planAiCredits}</div>
              </div>
              <div>
                <div className="text-lg font-bold" style={{ color: VUKZ.text }}>{storageLabel(cell.storage_mb)}</div>
                <div className="text-xs" style={{ color: VUKZ.textLight }}>{t.planStorage}</div>
              </div>
            </div>
          </div>

          {/* Team size — extras à la carte */}
          <div className="bg-white rounded-xl border overflow-hidden mb-6" style={{ borderColor: VUKZ.border }}>
            <div className="px-5 py-3 border-b" style={{ borderColor: VUKZ.border }}>
              <div className="text-sm font-semibold" style={{ color: VUKZ.text }}>{t.planTeamTitle}</div>
              <div className="text-xs" style={{ color: VUKZ.textLight }}>{t.planTeamSub}</div>
            </div>
            <PlanStepper
              label={t.planAthletes}
              sub={t.planPerExtra(formatBRL(cell.price_per_athlete_cents))}
              value={planSelection.athletes ?? cell.athlete_limit}
              min={cell.athlete_limit}
              onDec={() => bumpAthletes(-1)}
              onInc={() => bumpAthletes(1)}
            />
            {seatRoles.map(([k, seats]) => (
              <PlanStepper key={k}
                label={t.planRoleLabels[k] || k}
                sub={t.planPerExtra(formatBRL(cell.price_per_staff_cents))}
                value={planSelection.staffCounts?.[k] ?? seats}
                min={seats}
                onDec={() => bumpStaff(k, -1)}
                onInc={() => bumpStaff(k, 1)}
              />
            ))}
          </div>

          {/* Live price breakdown */}
          {breakdown && (
            <div className="bg-gray-50 rounded-xl border px-5 py-4 space-y-1.5" style={{ borderColor: VUKZ.border }}>
              <div className="flex justify-between text-sm">
                <span style={{ color: VUKZ.textLight }}>{t.planPriceBase} — {cell.plan_name}</span>
                <span className="font-medium" style={{ color: VUKZ.text }}>{formatBRL(cell.price_brl_cents)}</span>
              </div>
              {breakdown.extraAthletes > 0 && (
                <div className="flex justify-between text-sm">
                  <span style={{ color: VUKZ.textLight }}>{t.planPriceAthletes} ({t.planExtraTag(breakdown.extraAthletes)})</span>
                  <span className="font-medium" style={{ color: VUKZ.text }}>{formatBRL(breakdown.athletesCents)}</span>
                </div>
              )}
              {breakdown.extraStaff > 0 && (
                <div className="flex justify-between text-sm">
                  <span style={{ color: VUKZ.textLight }}>{t.planPriceStaff} ({t.planExtraTag(breakdown.extraStaff)})</span>
                  <span className="font-medium" style={{ color: VUKZ.text }}>{formatBRL(breakdown.staffCents)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm pt-2 border-t" style={{ borderColor: VUKZ.border }}>
                <span className="font-semibold" style={{ color: VUKZ.text }}>{t.planPriceTotal}</span>
                <span className="font-bold" style={{ color: VUKZ.text }}>
                  {formatBRL(breakdown.totalCents)} / {t.planMonthly}
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   STEP 5 — CONVIDAR EQUIPE  (was Step 6)
   ═══════════════════════════════════════════════════════════════════════════ */

const emptyInvite = { invite_email: '', invite_name: '', invite_role: 'ATLETA' };

const Step6Invites = ({ invites, setInvites }) => {
  const t = useWizardT();
  const addInvite = () => setInvites(prev => [...prev, { ...emptyInvite }]);
  const removeInvite = (idx) => setInvites(prev => prev.filter((_, i) => i !== idx));
  const updateInvite = (idx, key, val) => {
    setInvites(prev => prev.map((inv, i) => i === idx ? { ...inv, [key]: val } : inv));
  };

  return (
    <div>
      <SectionTitle icon={UserPlus} title={t.s7Title} subtitle={t.s7Sub} />

      <InfoBox>{t.s7Info}</InfoBox>

      {invites.map((inv, idx) => (
        <div key={idx} className="bg-white rounded-xl border p-4 mb-3 relative"
          style={{ borderColor: VUKZ.border }}>
          {invites.length > 0 && (
            <button onClick={() => removeInvite(idx)}
              className="absolute top-3 right-3 p-1 rounded-lg hover:bg-red-50 transition">
              <Trash2 size={14} className="text-red-400" />
            </button>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4">
            <InputField label={t.fullName} value={inv.invite_name}
              onChange={v => updateInvite(idx, 'invite_name', v)} placeholder={t.guestName} />
            <InputField label={t.emailLabel} value={inv.invite_email} type="email" icon={Mail}
              onChange={v => updateInvite(idx, 'invite_email', v)} placeholder="email@exemplo.com" required />
            <SelectField label={t.roleLabel} value={inv.invite_role}
              onChange={v => updateInvite(idx, 'invite_role', v)} options={INVITE_ROLES} required />
          </div>
        </div>
      ))}

      <button onClick={addInvite}
        className="w-full py-3 border-2 border-dashed rounded-xl text-sm font-medium flex items-center justify-center gap-2 hover:bg-gray-50 transition"
        style={{ borderColor: VUKZ.border, color: VUKZ.textLight }}>
        <Plus size={16} /> {t.addInvite}
      </button>

      {invites.length === 0 && (
        <p className="text-sm text-center mt-4" style={{ color: VUKZ.textLight }}>
          {t.noInvites}
        </p>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   STEP 6 — REVISÃO & ATIVAR  (was Step 7)
   ═══════════════════════════════════════════════════════════════════════════ */

const Step6Review = ({ formData, contacts, sports, planSelection, invites }) => {
  const t = useWizardT();
  const ReviewSection = ({ icon: Icon, title, children, count }) => (
    <div className="bg-white rounded-xl border p-5 mb-4" style={{ borderColor: VUKZ.border }}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={18} style={{ color: VUKZ.primary }} />
        <h3 className="font-semibold text-sm" style={{ color: VUKZ.text }}>{title}</h3>
        {count !== undefined && (
          <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#f3f4f6', color: VUKZ.textLight }}>
            {count}
          </span>
        )}
      </div>
      {children}
    </div>
  );

  const Field = ({ label, value }) => (
    value ? (
      <div className="flex justify-between py-1.5 border-b last:border-b-0" style={{ borderColor: '#f3f4f6' }}>
        <span className="text-sm" style={{ color: VUKZ.textLight }}>{label}</span>
        <span className="text-sm font-medium" style={{ color: VUKZ.text }}>{value}</span>
      </div>
    ) : null
  );

  const sportLabel = (code) => SPORT_OPTIONS.find(s => s.value === code)?.label || code;
  const orgLabel = (code) => ORG_TYPES.find(o => o.value === code)?.label || code;

  return (
    <div>
      <SectionTitle icon={CheckCircle2} title={t.s8Title} subtitle={t.s8Sub} />

      <InfoBox>{t.s8Info}</InfoBox>

      <ReviewSection icon={Building2} title={t.institutionalData}>
        <Field label={t.officialName} value={formData.legal_name} />
        <Field label={t.tradeName} value={formData.trade_name} />
        <Field label={t.typeLabel} value={orgLabel(formData.org_type)} />
        <Field label="CNPJ" value={formData.cnpj} />
        <Field label={t.locationLabel} value={[formData.city, formData.state, formData.country].filter(Boolean).join(', ')} />
        <Field label={t.primarySportLabel} value={sportLabel(formData.primary_sport)} />
        <Field label="Website" value={formData.website} />
        <Field label="Instagram" value={formData.instagram} />
      </ReviewSection>

      <ReviewSection icon={Users} title={t.contactsSection} count={contacts.length}>
        {contacts.length === 0 ? (
          <p className="text-sm" style={{ color: VUKZ.textLight }}>{t.noContacts}</p>
        ) : (
          contacts.map((c, i) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b last:border-b-0" style={{ borderColor: '#f3f4f6' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                style={{ backgroundColor: '#f3f4f6', color: VUKZ.primary }}>
                {(c.full_name || '?')[0].toUpperCase()}
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium" style={{ color: VUKZ.text }}>{c.full_name}</div>
                <div className="text-xs" style={{ color: VUKZ.textLight }}>
                  {CONTACT_ROLES.find(r => r.value === c.contact_role)?.label || c.contact_role}
                  {c.email && ` • ${c.email}`}
                </div>
              </div>
              {c.is_primary && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-600 font-medium">{t.primaryLabel}</span>
              )}
            </div>
          ))
        )}
      </ReviewSection>

      <ReviewSection icon={Dumbbell} title={t.sportsStructure} count={sports.length}>
        {sports.length === 0 ? (
          <p className="text-sm" style={{ color: VUKZ.textLight }}>{t.noTeams}</p>
        ) : (
          sports.map((s, i) => (
            <div key={i} className="py-3 border-b last:border-b-0" style={{ borderColor: '#f3f4f6' }}>
              <div className="flex items-center gap-3 mb-2">
                <div className="flex-1">
                  <div className="text-sm font-medium" style={{ color: VUKZ.text }}>
                    {sportLabel(s.sport_code)}
                  </div>
                  <div className="text-xs" style={{ color: VUKZ.textLight }}>
                    {[
                      s.athlete_count && `${s.athlete_count} ${t.athletesLabel}`,
                      s.coach_count && `${s.coach_count} ${t.coachesLabel}`,
                      s.training_location,
                    ].filter(Boolean).join(' • ') || t.noDetails}
                  </div>
                </div>
              </div>
              {(s.categories || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 ml-9">
                  {(s.categories || []).map(cat => (
                    <span key={cat} className="text-xs px-2.5 py-1 rounded-full font-medium"
                      style={{ backgroundColor: '#f0fdf4', color: VUKZ.primary, border: `1px solid ${VUKZ.primary}22` }}>
                      {CATEGORY_OPTIONS.find(c => c.value === cat)?.label || cat}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </ReviewSection>

      <ReviewSection icon={Package} title={t.planReviewTitle} count={planSelection?.cellData ? 1 : 0}>
        {!planSelection?.cellData ? (
          <p className="text-sm" style={{ color: VUKZ.textLight }}>{t.planNone}</p>
        ) : (() => {
          const c = planSelection.cellData;
          const athletes = planSelection.athletes ?? c.athlete_limit;
          const extraAth = Math.max(0, athletes - c.athlete_limit);
          const staffEntries = Object.entries(c.role_seats || {}).filter(([, s]) => s > 0);
          const extraStaff = staffEntries.reduce(
            (sum, [k, s]) => sum + Math.max(0, (planSelection.staffCounts?.[k] ?? s) - s), 0
          );
          const total = (c.price_brl_cents || 0)
            + extraAth * (c.price_per_athlete_cents || 0)
            + extraStaff * (c.price_per_staff_cents || 0);
          return (
            <div>
              <div className="flex items-center justify-between py-2">
                <div className="text-sm font-medium" style={{ color: VUKZ.text }}>{c.plan_name}</div>
                <div className="text-sm font-bold" style={{ color: VUKZ.text }}>
                  {formatBRL(total)} / {t.planMonthly}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="text-xs px-3 py-1.5 rounded-full font-medium"
                  style={{ backgroundColor: '#f3f4f6', color: VUKZ.text }}>
                  {athletes} {t.planAthletes}{extraAth > 0 ? ` (${t.planExtraTag(extraAth)})` : ''}
                </span>
                {staffEntries.map(([k, s]) => {
                  const val = planSelection.staffCounts?.[k] ?? s;
                  const extra = Math.max(0, val - s);
                  return (
                    <span key={k} className="text-xs px-3 py-1.5 rounded-full font-medium"
                      style={{ backgroundColor: '#f3f4f6', color: VUKZ.text }}>
                      {val} {t.planRoleLabels[k] || k}{extra > 0 ? ` (${t.planExtraTag(extra)})` : ''}
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </ReviewSection>

      <ReviewSection icon={UserPlus} title={t.invitesSection} count={invites.length}>
        {invites.length === 0 ? (
          <p className="text-sm" style={{ color: VUKZ.textLight }}>{t.noInvitesReview}</p>
        ) : (
          invites.map((inv, i) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b last:border-b-0" style={{ borderColor: '#f3f4f6' }}>
              <Mail size={14} style={{ color: VUKZ.textLight }} />
              <div className="flex-1">
                <div className="text-sm font-medium" style={{ color: VUKZ.text }}>
                  {inv.invite_name || inv.invite_email}
                </div>
                <div className="text-xs" style={{ color: VUKZ.textLight }}>
                  {inv.invite_email} • {INVITE_ROLES.find(r => r.value === inv.invite_role)?.label || inv.invite_role}
                </div>
              </div>
            </div>
          ))
        )}
      </ReviewSection>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN WIZARD COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

const ClubSetupWizardPage = () => {
  const navigate = useNavigate();
  const { lang } = useGovernanceLang();
  // C2 pre-req (jul/2026): discard/complete mutam onboarding_status no DB;
  // sem refresh, o rbacData em memoria fica stale e os gates (CentralGate/
  // RouteGuard) decidem com snapshot morto — discard 200 + navigate('/feed')
  // → /central lia 'pending' velho → funilava DE VOLTA pro wizard que o
  // usuario acabou de cancelar (reprovado ao vivo em 08/07; DB provou none).
  const { refreshRBAC } = useRBAC();
  const t = getWizardT(lang);

  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState(null);
  const [institutionId, setInstitutionId] = useState(null);
  const [onboardingId, setOnboardingId] = useState(null);
  const [loadingState, setLoadingState] = useState(true);
  const [completed, setCompleted] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);
  const [discarding, setDiscarding] = useState(false);

  // Step 1
  const [formData, setFormData] = useState({
    legal_name: '', trade_name: '', org_type: 'club', cnpj: '',
    country: 'Brasil', state: '', city: '', website: '', instagram: '',
    logo_url: '', primary_sport: 'swimming', other_sports: '',
  });

  // Step 2
  const [contacts, setContacts] = useState([{ ...emptyContact, contact_role: 'admin_master', is_primary: true }]);

  // Step 3
  const [sports, setSports] = useState([{ ...emptySport }]);

  // Step 4 — Equipe Técnica (NOVO)
  const [staffMembers, setStaffMembers] = useState([]);

  // Step 5 — Elenco (NOVO)
  const [rosterAthletes, setRosterAthletes] = useState([]);

  // Step 6 (was Modules) — Plano: célula do catálogo + dimensionamento.
  // { family, band, cell (objeto do payload), athletes, staffCounts{role:n} }
  const [planSelection, setPlanSelection] = useState({ family: null, band: null, cell: null, cellData: null, athletes: null, staffCounts: {} });

  // Step 7 — Convites
  const [invites, setInvites] = useState([]);

  // ── Assist bridge (workflow SETUP_CLUB, 26/08/2026) ────────────────────
  // form_context fatiado pela etapa atual (nunca o estado inteiro no prompt).
  // Patch de coleção é ATÔMICO (clubSetupFormContext.js). Aplicar ≠ salvar.
  const { registerAssistForm, unregisterAssistForm } = useAssistFormBridge();
  const assistStateRef = useRef(null);
  assistStateRef.current = {
    currentStep, formData, sports, staffMembers, rosterAthletes, planSelection, invites,
  };

  const handleAssistFill = useCallback((patch, fillForm) => {
    if (fillForm?.target && fillForm.target !== 'club_setup') {
      return { applied: [], rejected: [{ field: '*', reason: `sugestão destinada a outro formulário (${fillForm.target})` }] };
    }
    const { changes, applied, rejected } = applyClubSetupPatch(assistStateRef.current, patch);
    if (changes.formData) setFormData((prev) => ({ ...prev, ...changes.formData }));
    if (changes.categories) {
      setSports((prev) => prev.map((s, i) => (i === 0 ? { ...s, categories: changes.categories } : s)));
    }
    if (changes.staff) setStaffMembers((prev) => [...prev, ...changes.staff]);
    return { applied, rejected };
  }, []);

  // Multi-select staff_ids: ids são ÍNDICES da lista que o próprio usuário
  // digitou (eco do current_values — nunca invenção do LLM). Selecionados
  // recebem vínculo com TODAS as categorias do clube (v1 determinístico).
  const handleAssistSelection = useCallback((field, option) => {
    if (field !== 'staff_ids') return false;
    const opts = Array.isArray(option) ? option : [option];
    const idxs = new Set(opts.map((o) => Number(o.id)).filter(Number.isInteger));
    if (idxs.size === 0) return false;
    const cats = assistStateRef.current.sports[0]?.categories || [];
    setStaffMembers((prev) => prev.map((s, i) => (
      idxs.has(i) ? { ...s, category_ids: cats.slice() } : s
    )));
    return true;
  }, []);

  useEffect(() => {
    registerAssistForm({
      formId: 'club_setup',
      label: 'Configurar Clube',
      schemaVersion: 'club_setup.v1',
      getFormContext: () => buildClubSetupFormContext(assistStateRef.current),
      applyPatch: handleAssistFill,
      applySelection: handleAssistSelection,
    });
    return () => unregisterAssistForm('club_setup');
  }, [registerAssistForm, unregisterAssistForm, handleAssistFill, handleAssistSelection]);

  // ── Load existing state on mount ──────────────────────────────────────
  useEffect(() => {
    const loadState = async () => {
      try {
        const resp = await getOnboardingState();
        if (!resp?.success || !resp?.data?.onboarding) {
          setLoadingState(false);
          return; // No existing onboarding — fresh start
        }

        const { onboarding, contacts: c, sports_structure: sp, services: svc, invites: inv } = resp.data;

        setInstitutionId(onboarding.institution_id);
        setOnboardingId(onboarding.id);
        setCurrentStep(onboarding.current_step || 1);

        if (onboarding.setup_status === 'completed') {
          setCompleted(true);
        }

        // Restore step 1
        setFormData(prev => ({
          ...prev,
          legal_name: onboarding.legal_name || '',
          trade_name: onboarding.trade_name || '',
          org_type: onboarding.org_type || 'club',
          cnpj: onboarding.cnpj || '',
          country: onboarding.country || 'Brasil',
          state: onboarding.state || '',
          city: onboarding.city || '',
          website: onboarding.website || '',
          instagram: onboarding.instagram || '',
          logo_url: onboarding.logo_url || '',
          primary_sport: onboarding.primary_sport || 'swimming',
          other_sports: onboarding.other_sports || '',
        }));

        // Restore step 2
        if (c && c.length > 0) setContacts(c);

        // Restore step 3 (migrate old category_code → categories array)
        // Restore step 3 — group multiple rows by sport_code into one entry with categories array
        if (sp && sp.length > 0) {
          const grouped = {};
          sp.forEach(s => {
            const key = s.sport_code || 'swimming';
            if (!grouped[key]) {
              grouped[key] = { ...s, categories: [] };
            }
            if (s.category_code && !grouped[key].categories.includes(s.category_code)) {
              grouped[key].categories.push(s.category_code);
            }
            // Keep the highest athlete/coach counts across rows
            ['athlete_count', 'coach_count', 'support_staff_count'].forEach(f => {
              const cur = parseInt(grouped[key][f]) || 0;
              const val = parseInt(s[f]) || 0;
              if (val > cur) grouped[key][f] = s[f];
            });
          });
          setSports(Object.values(grouped));
        }

        // Step 6 (plano): sem persistência backend ainda — gancho plan_intent
        // na institution é migração futura; seleção vive no estado do wizard.

        // Restore step 5 (invites)
        if (inv && inv.length > 0) setInvites(inv);

      } catch (err) {
        console.error('[onboarding] Error loading state:', err);
      } finally {
        setLoadingState(false);
      }
    };
    loadState();
  }, []);

  // ── Discard onboarding (D1: descarte real, transacional no backend) ──
  const handleDiscard = async () => {
    setDiscarding(true);
    setError(null);
    try {
      const resp = await discardOnboarding();
      if (resp?.success) {
        // Snapshot fresco ANTES de navegar: o /central que recebe o usuario
        // le onboarding_status=none (livre) em vez do 'pending' morto.
        // Invariante de ordem do React: setRbacData enfileira antes do
        // update do router — todo render pos-navegacao ve o dado novo.
        await refreshRBAC();
        navigate('/feed', { replace: true });
      } else {
        setError(resp?.error || 'Não foi possível cancelar o cadastro');
        setShowDiscard(false);
      }
    } catch (err) {
      console.error('[onboarding] Error discarding:', err);
      setError(err?.response?.data?.error || 'Erro ao cancelar o cadastro');
      setShowDiscard(false);
    } finally {
      setDiscarding(false);
    }
  };

  // ── Start onboarding (if fresh) ──────────────────────────────────────
  const handleStart = async () => {
    if (institutionId) return institutionId; // Already started
    if (!formData.legal_name.trim()) {
      setError('Nome do clube é obrigatório');
      return null;
    }

    setSaving(true);
    setError(null);
    try {
      const resp = await startOnboarding({
        name: formData.legal_name.trim(),
        org_type: formData.org_type,
        primary_sport: formData.primary_sport,
      });
      if (resp?.success && resp.data) {
        setInstitutionId(resp.data.institution_id);
        setOnboardingId(resp.data.onboarding_id);
        return resp.data.institution_id; // Return for immediate use (avoids stale closure)
      } else {
        setError(resp?.error || 'Erro ao iniciar');
        return null;
      }
    } catch (err) {
      setError('Erro de conexão');
      return null;
    } finally {
      setSaving(false);
    }
  };

  // ── Save current step ────────────────────────────────────────────────
  const handleSaveStep = async (step, idOverride) => {
    const activeId = idOverride || institutionId;
    if (!activeId) {
      const newId = await handleStart();
      if (!newId) return;
      // Retry with the newly created id
      return handleSaveStep(step, newId);
    }

    setSaving(true);
    setError(null);
    try {
      switch (step) {
        case 1:
          await saveStep(1, { institution_id: activeId, ...formData });
          break;
        case 2:
          await saveContacts(activeId, contacts.filter(c => c.full_name.trim()));
          await saveStep(2, { institution_id: activeId });
          break;
        case 3:
          // Expand categories array → one row per category for backend compatibility
          const expandedSports = sports.filter(s => s.sport_code).flatMap(s => {
            const cats = s.categories || [];
            if (cats.length === 0) {
              return [{ ...s, category_code: null }];
            }
            return cats.map(cat => ({ ...s, category_code: cat }));
          });
          await saveSportsStructure(activeId, expandedSports);
          // Also save to club_categories via roster API
          try {
            const { AZURE } = await import('../../services/api');
            const catPayload = sports.filter(s => s.sport_code).flatMap(s =>
              (s.categories || []).map(cat => ({ sport_code: s.sport_code, category_code: cat }))
            );
            if (catPayload.length > 0) {
              await AZURE.post('/roster/categories/bulk', { categories: catPayload, institution_id: activeId });
            }
          } catch (catErr) {
            console.warn('[onboarding] Non-critical: could not sync club_categories', catErr);
          }
          await saveStep(3, { institution_id: activeId });
          break;
        case 4:
          // Save staff to roster API
          if (staffMembers.length > 0) {
            try {
              const { AZURE } = await import('../../services/api');
              for (const s of staffMembers) {
                await AZURE.post('/roster/staff', {
                  institution_id: activeId,
                  full_name: s.full_name,
                  email: s.email,
                  phone: s.phone,
                  staff_role: s.staff_role,
                  category_ids: s.category_ids || [],
                });
              }
            } catch (staffErr) {
              console.error('[onboarding] Error saving staff:', staffErr);
              throw staffErr;
            }
          }
          await saveStep(4, { institution_id: activeId });
          break;
        case 5:
          // Save athletes to roster API
          if (rosterAthletes.length > 0) {
            try {
              const { AZURE } = await import('../../services/api');
              for (const a of rosterAthletes) {
                await AZURE.post('/roster/athletes', {
                  institution_id: activeId,
                  full_name: a.full_name,
                  birth_date: a.birth_date || null,
                  gender: a.gender || null,
                  // category_id and staff assignments handled by backend
                });
              }
            } catch (athErr) {
              console.error('[onboarding] Error saving athletes:', athErr);
              throw athErr;
            }
          }
          await saveStep(5, { institution_id: activeId });
          break;
        case 6:
          // Plano: seleção vive no estado (persistência plan_intent = migração
          // futura na institution; sem subscription — amarra do passo 5).
          await saveStep(6, { institution_id: activeId });
          break;
        case 7:
          const validInvites = invites.filter(i => i.invite_email.trim());
          if (validInvites.length > 0) {
            await saveInvites(activeId, validInvites);
          }
          await saveStep(7, { institution_id: activeId });
          break;
        default:
          break;
      }
    } catch (err) {
      console.error(`[onboarding] Error saving step ${step}:`, err);
      setError('Erro ao salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  // ── Navigate between steps ───────────────────────────────────────────
  const goNext = async () => {
    // Step 1 special: must create institution first
    let activeId = institutionId;
    if (currentStep === 1 && !activeId) {
      activeId = await handleStart();
      if (!activeId) return; // Failed
    }

    await handleSaveStep(currentStep, activeId);
    if (currentStep < 8) setCurrentStep(prev => prev + 1);
  };

  const goPrev = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  const goToStep = (step) => {
    // Only allow going to visited steps or current +1
    if (step <= currentStep || step === currentStep + 1) {
      setCurrentStep(step);
    }
  };

  // ── Complete onboarding ──────────────────────────────────────────────
  const handleComplete = async () => {
    setCompleting(true);
    setError(null);
    try {
      // Save step 7 first
      await saveStep(7, { institution_id: institutionId });
      // Then complete
      const resp = await completeOnboarding(institutionId);
      if (resp?.success) {
        // Snapshot fresco ANTES da tela de sucesso: os botoes dela navegam
        // pra rotas role-gated (/manager) — com rbacData stale ('pending'),
        // o funil do C2 devolveria o recem-concluido pro wizard que ele
        // acabou de completar. Com 'completed' fresco, o funil nao pega.
        await refreshRBAC();
        setCompleted(true);
      } else {
        setError(resp?.error || 'Erro ao ativar');
      }
    } catch (err) {
      setError('Erro ao ativar o clube');
    } finally {
      setCompleting(false);
    }
  };

  // ── Step validation ──────────────────────────────────────────────────
  const isStepValid = (step) => {
    switch (step) {
      case 1: return formData.legal_name.trim().length > 0 && formData.org_type && formData.primary_sport;
      case 2: return contacts.some(c => c.full_name.trim());
      case 3: return sports.some(s => s.sport_code);
      case 4: return true; // Staff — optional (can add later)
      case 5: return true; // Athletes — optional (can add later)
      case 6: return true; // Modules — optional
      case 7: return true; // Invites — optional
      case 8: return true; // Review
      default: return false;
    }
  };

  // ── Loading state ────────────────────────────────────────────────────
  if (loadingState) {
    return (
      <PageShell>
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin mb-4" style={{ color: VUKZ.primary }} />
          <p className="text-sm" style={{ color: VUKZ.textLight }}>{t.loading}</p>
        </div>
      </PageShell>
    );
  }

  // ── Completed state — 3-level gestor model ─────────────────────────
  if (completed) {
    // Gestor levels: pendente → ativo → validado/vinculado
    // After completing wizard = "ativo" (can manage club, invite team)
    // After claim approved = "validado" (can access official data)
    const isVinculado = false; // TODO: check claim status from backend

    return (
      <PageShell>
        <div className="max-w-2xl mx-auto py-12">
          {/* Success header */}
          <div className="text-center mb-10">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ background: VUKZ_GRADIENT }}>
              <Sparkles size={32} className="text-white" />
            </div>
            <h1 className="text-3xl font-extrabold mb-2" style={{ color: VUKZ.text }}>
              {t.clubActivated}
            </h1>
            <p className="text-base" style={{ color: VUKZ.textLight }}>
              {t.clubActivatedSub}
            </p>
            <div className="inline-flex items-center gap-2 mt-3 px-4 py-1.5 rounded-full text-xs font-bold"
              style={{ backgroundColor: '#f0fdf4', color: VUKZ.primary, border: `1px solid ${VUKZ.primary}22` }}>
              <Shield size={12} /> {t.activeManager}
            </div>
          </div>

          {/* Available actions */}
          <div className="bg-white rounded-xl border p-6 mb-4" style={{ borderColor: VUKZ.border }}>
            <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{ color: VUKZ.text }}>
              <CheckCircle2 size={16} style={{ color: '#16a34a' }} /> {t.availableNow}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <GatedAction enabled onClick={() => navigate('/manager')} icon={Building2}>
                {t.managementPanel}
              </GatedAction>
              <GatedAction enabled onClick={() => navigate('/feed')} icon={Users} variant="secondary">
                {t.viewFeed}
              </GatedAction>
            </div>
          </div>

          {/* Gated actions — require claim/validation */}
          <div className="bg-white rounded-xl border p-6" style={{ borderColor: VUKZ.border }}>
            <h3 className="text-sm font-bold mb-1 flex items-center gap-2" style={{ color: VUKZ.text }}>
              <Lock size={16} style={{ color: '#d97706' }} /> {t.requiresValidation}
            </h3>
            <p className="text-xs mb-4" style={{ color: VUKZ.textLight }}>
              {t.requiresValidationSub}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <GatedAction enabled={isVinculado} reason={t.requiresValidationSub} href="/manager/claim" icon={Link2} variant="gold">
                {t.linkOfficialDataGated}
              </GatedAction>
              <GatedAction enabled={isVinculado} reason={t.requiresValidationSub} icon={Database}>
                {t.manageOfficialAthletes}
              </GatedAction>
              <GatedAction enabled={isVinculado} reason={t.requiresValidationSub} icon={UserCog}>
                {t.fullInstitutionalPanel}
              </GatedAction>
              <GatedAction enabled={isVinculado} reason={t.requiresValidationSub} icon={Shield}>
                {t.approveClubMembers}
              </GatedAction>
            </div>

            {/* CTA to start claim */}
            {!isVinculado && (
              <div className="mt-5 p-4 rounded-xl border-2 border-dashed flex items-start gap-3"
                style={{ borderColor: '#d97706', backgroundColor: '#fffbeb' }}>
                <Search size={18} className="mt-0.5 flex-shrink-0" style={{ color: '#d97706' }} />
                <div>
                  <p className="text-sm font-bold mb-1" style={{ color: VUKZ.text }}>
                    {t.requestOfficialLinkTitle}
                  </p>
                  <p className="text-xs mb-2" style={{ color: VUKZ.textLight }}>
                    {t.requestOfficialLinkDesc}
                  </p>
                  <a href="/manager/claim"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition hover:opacity-90"
                    style={{ backgroundColor: '#d97706' }}>
                    <Link2 size={14} /> {t.requestLink} <ArrowRight size={14} />
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </PageShell>
    );
  }

  // ── Render step content ──────────────────────────────────────────────

  // Build flat categories list from sports structure (for Step 4 & 5 pickers)
  const flatCategories = sports.flatMap(s =>
    (s.categories || []).map((code, idx) => ({
      _tempId: `${s.sport_code}_${code}_${idx}`,
      category_id: null, // Will get real IDs after save
      sport_code: s.sport_code,
      category_code: code,
      category_name: null,
    }))
  );

  const renderStep = () => {
    switch (currentStep) {
      case 1: return <Step1Institution formData={formData} setFormData={setFormData} />;
      case 2: return <Step2Contacts contacts={contacts} setContacts={setContacts} />;
      case 3: return <Step3Sports sports={sports} setSports={setSports} primarySport={formData.primary_sport} institutionId={institutionId} />;
      case 4: return <Step4Staff staffMembers={staffMembers} setStaffMembers={setStaffMembers} categories={flatCategories} />;
      case 5: return <Step5Roster athletes={rosterAthletes} setAthletes={setRosterAthletes} categories={flatCategories} staffMembers={staffMembers} />;
      case 6: return <StepPlan planSelection={planSelection} setPlanSelection={setPlanSelection} />;
      case 7: return <Step6Invites invites={invites} setInvites={setInvites} />;
      case 8: return <Step6Review formData={formData} contacts={contacts} sports={sports}
                        planSelection={planSelection} invites={invites}
                        staffCount={staffMembers.length} athleteCount={rosterAthletes.length} />;
      default: return null;
    }
  };

  const progressPct = Math.round((currentStep / 8) * 100);

  return (
    <WizardLangCtx.Provider value={lang}>
    <PageShell>
      {/* ── HEADER ── */}
      <div className="bg-white rounded-2xl border mb-6 overflow-hidden" style={{ borderColor: VUKZ.border }}>
        <div className="p-5 pb-4" style={{ background: VUKZ_GRADIENT }}>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-extrabold text-white">
              {t.headerTitle}
            </h1>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold"
              style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff', backdropFilter: 'blur(4px)' }}>
              <Shield size={12} /> Gestor
            </span>
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => navigate('/clubes')}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold transition hover:opacity-90"
                style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff' }}>
                {t.saveExit}
              </button>
              <button
                onClick={() => setShowDiscard(true)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold transition hover:opacity-90"
                style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.75)' }}>
                {t.discardSetup}
              </button>
            </div>
          </div>
          <p className="text-sm text-gray-300">
            {t.headerSub}
          </p>
        </div>

        {/* Progress bar */}
        <div className="px-5 pt-4 pb-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium" style={{ color: VUKZ.textLight }}>
              {t.stepOf(currentStep)}
            </span>
            <span className="text-xs font-bold" style={{ color: VUKZ.primary }}>
              {progressPct}%
            </span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#f3f4f6' }}>
            <div className="h-full rounded-full transition-all duration-500"
              style={{ width: `${progressPct}%`, background: VUKZ_GRADIENT }} />
          </div>
        </div>

        {/* Step indicators */}
        <div className="px-5 py-3 flex gap-1 overflow-x-auto scrollbar-hide">
          {getSteps(t).map(s => {
            const Icon = s.icon;
            const isActive = s.num === currentStep;
            const isDone = s.num < currentStep;
            const isClickable = s.num <= currentStep || s.num === currentStep + 1;

            return (
              <button
                key={s.num}
                onClick={() => isClickable && goToStep(s.num)}
                disabled={!isClickable}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                  isActive
                    ? 'bg-gray-900 text-white shadow-md'
                    : isDone
                      ? 'bg-gray-100 text-gray-800'
                      : isClickable
                        ? 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                        : 'bg-gray-50 text-gray-300 cursor-not-allowed'
                }`}>
                {isDone ? (
                  <CheckCircle2 size={14} className="text-green-500" />
                ) : (
                  <Icon size={14} />
                )}
                <span className="hidden sm:inline">{s.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── ERROR ── */}
      {error && (
        <div className="flex items-center gap-3 p-4 rounded-xl mb-4 border"
          style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca' }}>
          <AlertCircle size={18} className="text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X size={14} className="text-red-400" />
          </button>
        </div>
      )}

      {/* ── STEP CONTENT ── */}
      <div className="bg-white rounded-2xl border p-6 mb-6" style={{ borderColor: VUKZ.border }}>
        {renderStep()}
      </div>

      {/* ── NAVIGATION ── */}
      <div className="flex items-center justify-between">
        <button
          onClick={goPrev}
          disabled={currentStep === 1}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition ${
            currentStep === 1
              ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
              : 'bg-white border-2 hover:bg-gray-50'
          }`}
          style={currentStep > 1 ? { borderColor: VUKZ.border, color: VUKZ.text } : {}}>
          <ChevronLeft size={16} />
          {t.back}
        </button>

        <div className="flex gap-3">
          {currentStep < 8 ? (
            <button
              onClick={goNext}
              disabled={saving || !isStepValid(currentStep)}
              className="flex items-center gap-2 px-8 py-3 rounded-xl font-bold text-sm text-white transition hover:opacity-90 disabled:opacity-50"
              style={{ background: VUKZ_GRADIENT }}>
              {saving ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <>
                  {t.next}
                  <ChevronRight size={16} />
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleComplete}
              disabled={completing}
              className="flex items-center gap-2 px-8 py-3 rounded-xl font-bold text-sm text-white transition hover:opacity-90 disabled:opacity-50"
              style={{ background: VUKZ_GRADIENT }}>
              {completing ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <>
                  <Sparkles size={16} />
                  {t.activateClub}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* ── DISCARD CONFIRMATION MODAL ── */}
      {showDiscard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
            <h3 className="text-lg font-bold mb-2" style={{ color: VUKZ.text }}>
              {t.discardTitle}
            </h3>
            <p className="text-sm mb-6" style={{ color: VUKZ.textLight }}>
              {t.discardBody}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDiscard(false)}
                disabled={discarding}
                className="px-4 py-2 rounded-xl text-sm font-semibold border-2 hover:bg-gray-50 transition"
                style={{ borderColor: VUKZ.border, color: VUKZ.text }}>
                {t.discardKeep}
              </button>
              <button
                onClick={handleDiscard}
                disabled={discarding}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white transition hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: '#dc2626' }}>
                {discarding ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    {t.discarding}
                  </>
                ) : t.discardConfirm}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding Assistant */}
      <OnboardingAssistant
        context="wizard"
        step={currentStep}
        formData={formData}
        errors={error ? { general: error } : {}}
        onApplyAction={(action) => {
          if (action.type === ActionType.FILL_FIELDS && action.fields) {
            setFormData(prev => ({ ...prev, ...action.fields }));
          }
          if (action.type === ActionType.SELECT_SPORT && action.sportId) {
            setFormData(prev => ({ ...prev, primary_sport: action.sportId }));
          }
          if (action.type === ActionType.SEARCH_CLUB && action.query) {
            // Navigate to step 4 and trigger search
            setCurrentStep(4);
          }
          if (action.type === ActionType.ADD_INVITE && action.inviteData) {
            setInvites(prev => [...prev, { ...action.inviteData, invite_name: '' }]);
          }
        }}
        onNavigateStep={(s) => {
          if (s >= 1 && s <= 7) setCurrentStep(s);
        }}
      />
    </PageShell>
    </WizardLangCtx.Provider>
  );
};

export default ClubSetupWizardPage;
